import { Link } from "react-router-dom";
import { ArrowRight, FileText, Search, ChefHat, BarChart3, Sprout, CookingPot } from "lucide-react";
import { FEATURES, WHY_CARD, JOURNEY, featurePath, M } from "../../data/site";
import { FadeUp } from "../site/motion";
import { VisionNote } from "../site/Badges";
import { SectionHead, Container } from "./SectionHead";

export const FeatureCards = () => {
  const cards = [FEATURES[0], FEATURES[1], WHY_CARD, ...FEATURES.slice(2)];
  return (
    <Container testId="feature-cards" className="!pt-10">
      <SectionHead title={<>Your Kitchen <span className="text-grad">Companion</span></>} sub="Practical support, personalized to your home and your life." />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map((c, i) => (
          <FadeUp key={c.title} delay={i * 0.06} className="h-full">
            <Link to={featurePath(c.slug) + (c === WHY_CARD ? "#why" : "")} data-testid={`feature-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="glass group flex h-full flex-col overflow-hidden rounded-2xl transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_0_40px_rgba(34,211,238,0.3)]">
              <div className="aspect-[10/9] overflow-hidden"><img src={c.card} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
              <div className="flex flex-1 flex-col p-5">
                <c.icon className="h-8 w-8 text-cyan-200" strokeWidth={1.3} />
                <h3 className="mt-3 font-display text-xl font-semibold leading-tight text-white">{c.title}</h3>
                <p className="mt-2 flex-1 text-sm text-slate-300">{c.short}</p>
                <span className="mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </Container>
  );
};

const JICONS = [FileText, Search, ChefHat, BarChart3];

export const Journey = () => (
  <Container testId="cooking-journey">
    <SectionHead eyebrow="How it works" title={<>A simpler <span className="text-grad">cooking journey</span></>} sub="From what you have to what’s next — Honesti is with you at every step." />
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
      {JOURNEY.map((j, i) => {
        const Icon = JICONS[i];
        return (
          <FadeUp key={j.title} delay={i * 0.1} className="relative h-full">
            <Link to={j.to} data-testid={`journey-step-${j.title.toLowerCase()}`} className="glass group flex h-full flex-col overflow-hidden rounded-2xl transition-transform duration-500 hover:-translate-y-2">
              <div className="h-40 overflow-hidden"><img src={j.img} alt={j.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
              <div className="flex gap-4 p-5">
                <Icon className="h-8 w-8 shrink-0 text-cyan-200" strokeWidth={1.3} />
                <div><p className="font-mono text-xs text-cyan-300">0{i + 1}</p><h3 className="font-display text-2xl font-semibold text-white">{j.title}</h3><p className="mt-1 text-sm text-slate-300">{j.text}</p></div>
              </div>
            </Link>
            {i < 3 && <ArrowRight className="absolute -right-7 top-20 hidden h-6 w-6 text-cyan-300 lg:block" />}
          </FadeUp>
        );
      })}
    </div>
  </Container>
);

const VISION = [
  { icon: Sprout, title: "HonestGrow™ University", text: "Practical guidance for a healthier home — from cooking and nutrition to wellness, routines and everyday life.", img: M("c_v_grow.jpg"), to: "/advanced-learning/honestgrow-university" },
  { icon: CookingPot, title: "Little Chefs Academy", text: "Fun, age-appropriate cooking and nutrition education to help kids build confidence, creativity and lifelong healthy habits.", img: M("c_v_chefs.jpg"), to: "/advanced-learning/little-chefs-academy" },
];

export const Vision = () => (
  <Container id="vision" testId="vision-ahead">
    <SectionHead eyebrow="Advanced learning" title={<>The vision <span className="text-grad">ahead</span></>} sub="More learning, stronger families and brighter tomorrows." right={<VisionNote />} />
    <div className="grid gap-5 lg:grid-cols-2">
      {VISION.map((v, i) => (
        <FadeUp key={v.title} delay={i * 0.12} className="h-full">
          <div className="glass group relative grid h-full overflow-hidden rounded-[1.75rem] sm:grid-cols-[1.2fr_1fr]">
            <div className="relative z-10 p-7 lg:p-9">
              <v.icon className="h-10 w-10 text-cyan-200" strokeWidth={1.2} />
              <h3 className="mt-5 font-display text-3xl font-semibold text-white">{v.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">{v.text}</p>
              <Link to={v.to} data-testid={`vision-explore-${i}`} className="btn-ghost mt-7 !py-2.5 text-sm">Explore the Vision <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="relative min-h-[220px] overflow-hidden">
              <img src={v.img} alt={v.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-deep/70 to-transparent sm:bg-gradient-to-r sm:from-deep sm:via-deep/20" />
            </div>
          </div>
        </FadeUp>
      ))}
    </div>
  </Container>
);
